import React, { useState, useEffect } from 'react';
import { usePersona } from '@/contexts/PersonaContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { DashboardPage } from '@/components/Pages/DashboardPage';
import { ManagerDashboard } from './ManagerDashboard';
import { cn } from '@/lib/utils';
import { Lock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface PersonaDashboardProps {
  onNavigate?: (path: string) => void;
}

export const PersonaDashboard: React.FC<PersonaDashboardProps> = ({ onNavigate }) => {
  const { currentPersona } = usePersona();
  const { canViewPersonalHomepage, canViewTeamDashboard, canViewDepartmentDashboard } = usePermissions();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayPersona, setDisplayPersona] = useState(currentPersona);

  // Handle smooth transition between personas
  useEffect(() => {
    if (currentPersona !== displayPersona) {
      setIsTransitioning(true);
      
      // Wait for fade out
      const timer = setTimeout(() => {
        setDisplayPersona(currentPersona);
        setIsTransitioning(false);
      }, 150); // Half of the transition duration

      return () => clearTimeout(timer);
    }
  }, [currentPersona, displayPersona]);

  const hasPersonal = canViewPersonalHomepage();
  const hasTeamOrDepartment = canViewTeamDashboard() || canViewDepartmentDashboard();

  if (!hasPersonal && !hasTeamOrDepartment) {
    return (
      <div className="p-6">
        <Alert variant="destructive" showIcon={false}>
          <Lock className="h-4 w-4" />
          <AlertDescription>
            You do not have permission to view the personal homepage. Please contact your administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Homepage Content with Transition - Toggle now integrated in each homepage header */}
      <div
        className={cn(
          'transition-opacity duration-300',
          isTransitioning ? 'opacity-0' : 'opacity-100'
        )}
      >
        {hasPersonal && displayPersona === 'personal' ? (
          <DashboardPage onNavigate={onNavigate} />
        ) : (
          <ManagerDashboard onNavigate={onNavigate} />
        )}
      </div>
    </div>
  );
};
